import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Star } from "lucide-react";
import { API } from "../../lib/constants";

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5.0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [source, setSource] = useState("loading");

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data.reviews || []);
        setRating(response.data.rating || 5.0);
        setTotalReviews(response.data.total_reviews || 0);
        setSource(response.data.source || "testimonials");
      } catch (error) {
        console.error("Error fetching reviews:", error);
        // Fallback reviews
        setReviews([
          { author: "Sarah M.", rating: 5, text: "Absolutely amazing service! My car looks brand new.", relative_time: "Recently" },
          { author: "James T.", rating: 5, text: "Best mobile detailing service in Melbourne!", relative_time: "Recently" },
          { author: "Michelle K.", rating: 5, text: "RainXpress is by far the best detailer I've used.", relative_time: "Recently" }
        ]);
        setSource("testimonials");
      }
    };
    fetchReviews();
  }, []);

  return (
    <section className="section-spacing bg-slate-950" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Customer <span className="gradient-text">Reviews</span>
          </h2>
          <div className="flex justify-center items-center gap-2 mb-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`w-6 h-6 ${i < Math.round(rating) ? 'text-yellow-400' : 'text-slate-600'}`} 
                  fill={i < Math.round(rating) ? "currentColor" : "none"} 
                />
              ))}
            </div>
            <span className="text-xl font-bold text-white">{rating.toFixed(1)}</span>
          </div>
          {totalReviews > 0 && (
            <p className="text-slate-400 text-sm">Based on {totalReviews} reviews</p>
          )}
        </motion.div>

        <div className={`grid grid-cols-1 ${reviews.length >= 2 ? 'md:grid-cols-2' : ''} ${reviews.length >= 3 ? 'md:grid-cols-3' : ''} gap-6 ${reviews.length === 1 ? 'max-w-lg mx-auto' : ''}`}>
          {reviews.slice(0, 3).map((review, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="review-card"
              data-testid={`review-${i}`}
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(review.rating || 5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              <p className="text-slate-300 mb-4 line-clamp-4">"{review.text}"</p>
              <div className="flex items-center gap-3 text-sm">
                {review.photo_uri && (
                  <img 
                    src={review.photo_uri} 
                    alt={review.author} 
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )}
                <div className="flex-1 flex justify-between items-center">
                  <span className="font-medium text-white">{review.author}</span>
                  <span className="text-slate-500">{review.relative_time || review.date}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-slate-500 text-sm mt-8">
          {source === "google" ? (
            <a 
              href="https://share.google/jwoJAdp1uC4sM5nMr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 hover:text-cyan-400 transition"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              View All Reviews on Google →
            </a>
          ) : (
            "Customer testimonials"
          )}
        </p>
      </div>
    </section>
  );
};

export default ReviewsSection;
